import * as THREE from 'three';

/**
 * The faces of a flight that feet can stand on, sorted onto a plan grid for the walk's footing (`Viewer.stairSupport`).
 * The walk reads the heights under nine points round the foot at every step, and a ray cast down through a model tests
 * every triangle of it — a millisecond a ray through the grand staircase's 17 000 — where a cell of the grid holds a
 * handful. The faces are the ones such a ray meets as three.js casts it (the default layer, each material's side and
 * groups, the draw range, morphs and instances) and turns up at least `TREAD_UP`, in world space; they are gathered again
 * when a mesh of the node moves, changes its geometry or material, or comes or goes.
 */
export interface Treads {
  /** The heights of the faces over a plan point, none higher than `top`. */
  heights(x: number, z: number, top: number): number[];
}

/** How far a face turns up (its normal's height) for feet to stand on it. */
export const TREAD_UP = .7;
/** A cell of the grid, in metres; a big model gets bigger cells rather than more of them. */
const CELL = .1, MAX_CELLS = 256;
/** A point on the edge between two faces is over both of them. */
const EDGE = 1e-7;

interface Index extends Treads { key: unknown[] }
const indexes = new WeakMap<THREE.Object3D, Index>();
const rayLayers = new THREE.Layers();
const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3(), normal = new THREE.Vector3();
const normalMatrix = new THREE.Matrix3(), matrix = new THREE.Matrix4(), instanceMatrix = new THREE.Matrix4();
const NONE: Index = { key: [], heights: () => [] };

/** The meshes a ray cast down through `node` tests. */
function eachMesh(node: THREE.Object3D, visit: (mesh: THREE.Mesh) => void): void {
  node.traverse(object => {
    const mesh = object as THREE.Mesh;
    if (mesh.isMesh && mesh.material && mesh.layers.test(rayLayers)) visit(mesh);
  });
}

/** Everything the faces depend on, mesh by mesh. */
function keyOf(node: THREE.Object3D): unknown[] {
  const key: unknown[] = [];
  eachMesh(node, mesh => {
    const geometry = mesh.geometry, instanced = mesh as THREE.InstancedMesh, position = geometry.attributes.position;
    key.push(mesh, geometry, position && ('isInterleavedBufferAttribute' in position ? position.data.version : position.version),
      geometry.index?.version ?? -1, geometry.drawRange.start,
      geometry.drawRange.count, geometry.groups.length, ...(Array.isArray(mesh.material) ? mesh.material : [mesh.material]).map(material => material?.side),
      ...mesh.matrixWorld.elements, ...(mesh.morphTargetInfluences ?? []),
      instanced.isInstancedMesh ? instanced.count : -1, instanced.isInstancedMesh ? instanced.instanceMatrix.version : -1);
  });
  return key;
}

const sameKey = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((value, i) => Object.is(value, b[i]));

function build(node: THREE.Object3D, key: unknown[]): Index {
  // Per face: its third corner and the plan-to-barycentric rows, then the corners' heights.
  const faces: number[] = [], boxes: number[] = [];
  eachMesh(node, mesh => {
    const geometry = mesh.geometry, position = geometry.attributes.position, index = geometry.index;
    if (!position) return;
    const count = index ? index.count : position.count, range = geometry.drawRange, materials = mesh.material;
    const spans: Array<[number, number, THREE.Material | undefined]> = Array.isArray(materials)
      ? geometry.groups.map(group => [Math.max(group.start, range.start),
        Math.min(count, group.start + group.count, range.start + range.count), materials[group.materialIndex ?? 0]])
      : [[Math.max(0, range.start), Math.min(count, range.start + range.count), materials]];
    const instanced = (mesh as THREE.InstancedMesh).isInstancedMesh ? mesh as THREE.InstancedMesh : undefined;
    for (let copy = 0; copy < (instanced?.count ?? 1); copy++) {
      if (instanced) { instanced.getMatrixAt(copy, instanceMatrix); matrix.multiplyMatrices(mesh.matrixWorld, instanceMatrix); }
      else matrix.copy(mesh.matrixWorld);
      normalMatrix.getNormalMatrix(matrix);
      for (const [start, end, material] of spans) {
        // A back-side material shows the ray only the faces turned away from it: none of them turned up.
        if (!material || material.side === THREE.BackSide) continue;
        for (let i = start; i + 2 < end; i += 3) {
          const a = index ? index.getX(i) : i, b = index ? index.getX(i + 1) : i + 1, c = index ? index.getX(i + 2) : i + 2;
          mesh.getVertexPosition(a, vA); mesh.getVertexPosition(b, vB); mesh.getVertexPosition(c, vC);
          THREE.Triangle.getNormal(vA, vB, vC, normal);
          if (normal.applyMatrix3(normalMatrix).normalize().y < TREAD_UP) continue;
          vA.applyMatrix4(matrix); vB.applyMatrix4(matrix); vC.applyMatrix4(matrix);
          const det = (vB.z - vC.z) * (vA.x - vC.x) + (vC.x - vB.x) * (vA.z - vC.z);
          if (Math.abs(det) < 1e-12) continue;
          faces.push(vC.x, vC.z, (vB.z - vC.z) / det, (vC.x - vB.x) / det, (vC.z - vA.z) / det, (vA.x - vC.x) / det, vA.y, vB.y, vC.y);
          boxes.push(Math.min(vA.x, vB.x, vC.x), Math.min(vA.z, vB.z, vC.z), Math.max(vA.x, vB.x, vC.x), Math.max(vA.z, vB.z, vC.z));
        }
      }
    }
  });
  const count = boxes.length / 4;
  if (!count) return { ...NONE, key };
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (let f = 0; f < count; f++) {
    minX = Math.min(minX, boxes[4 * f]); minZ = Math.min(minZ, boxes[4 * f + 1]);
    maxX = Math.max(maxX, boxes[4 * f + 2]); maxZ = Math.max(maxZ, boxes[4 * f + 3]);
  }
  const cell = Math.max(CELL, (maxX - minX) / MAX_CELLS, (maxZ - minZ) / MAX_CELLS);
  const columns = Math.floor((maxX - minX) / cell) + 1, rows = Math.floor((maxZ - minZ) / cell) + 1;
  const column = (x: number) => Math.min(columns - 1, Math.max(0, Math.floor((x - minX) / cell)));
  const row = (z: number) => Math.min(rows - 1, Math.max(0, Math.floor((z - minZ) / cell)));
  // Each face goes into every cell its plan box touches, a hair wider so a point on its edge finds it.
  const cover = (f: number, visit: (cellIndex: number) => void) => {
    const x0 = column(boxes[4 * f] - 1e-6), x1 = column(boxes[4 * f + 2] + 1e-6), z0 = row(boxes[4 * f + 1] - 1e-6), z1 = row(boxes[4 * f + 3] + 1e-6);
    for (let j = z0; j <= z1; j++) for (let i = x0; i <= x1; i++) visit(j * columns + i);
  };
  const offsets = new Int32Array(columns * rows + 1);
  for (let f = 0; f < count; f++) cover(f, c => { offsets[c + 1]++; });
  for (let c = 0; c < columns * rows; c++) offsets[c + 1] += offsets[c];
  const items = new Int32Array(offsets[columns * rows]), filled = offsets.slice(0, columns * rows);
  for (let f = 0; f < count; f++) cover(f, c => { items[filled[c]++] = f; });
  const table = Float64Array.from(faces);
  return {
    key,
    heights(x, z, top) {
      const out: number[] = [], i = Math.floor((x - minX) / cell), j = Math.floor((z - minZ) / cell);
      if (i < 0 || j < 0 || i >= columns || j >= rows) return out;
      for (let k = offsets[j * columns + i], end = offsets[j * columns + i + 1]; k < end; k++) {
        const f = items[k] * 9, dx = x - table[f], dz = z - table[f + 1];
        const a = table[f + 2] * dx + table[f + 3] * dz, b = table[f + 4] * dx + table[f + 5] * dz, c = 1 - a - b;
        if (a < -EDGE || b < -EDGE || c < -EDGE) continue;
        const y = a * table[f + 6] + b * table[f + 7] + c * table[f + 8];
        if (y <= top) out.push(y);
      }
      return out;
    },
  };
}

/** The tread faces of `node` as it stands now (its world matrices updated); gathered once and kept while it stays so. */
export function treadsOf(node: THREE.Object3D): Treads {
  const key = keyOf(node), known = indexes.get(node);
  if (known && sameKey(known.key, key)) return known;
  const index = build(node, key);
  indexes.set(node, index);
  return index;
}
